import React, { useState, useEffect } from "react";
import { Container, TableContainer, Paper, Table, TableHead, CircularProgress, TableRow, InputAdornment, TextField, IconButton, TableCell, TableBody, Tooltip, Button, Box, Typography, Modal } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import base64 from "base64-js";
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function UserGroup() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUserGroup, setSelectedUserGroup] = useState({
    userGroupId: null,
    userGroupName: "",
  });
  const navigate = useNavigate();
  const [authorization, setAuthorization] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      // Ensure the encodedFormId is properly padded
      const paddedEncodedFormId = encodedFormId.padEnd(
        Math.ceil(encodedFormId.length / 4) * 4,
        "="
      );
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };
  const formId = decodeFormId(encodedFormId);

 const fetchData = async () => {
  try {
    setIsLoading(true); // Start loading
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/CPanel/GetUserGroup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        userGroupId: sessionStorage.getItem("userGroupId"),
      }),
    });
    if (response.ok) {
      const responseData = await response.json();
      setData(responseData);
      setFilteredData(responseData);
    } else {
      console.error("Failed to fetch user group data");
      // alert("Failed to fetch user group data");
      setSnackbarMessage('Failed to fetch user group data');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  } catch (error) {
    console.error("API request error:", error);
    // alert("An error occurred. Please try again later.");
    setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
  } finally {
    setIsLoading(false); // Stop loading
  }
};


  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
      } else {
        console.error("Authorization failed");
        // alert("Authorization failed");
        setSnackbarMessage('Authorization failed');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
        setAuthorization({ isPermission: 0 });
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setAuthorization({ isPermission: 0 });
    }
  };

  useEffect(() => {
    if (formId) {
      Authorizer();
      if (authorization?.uRead === 1) {
        fetchData();
      }
    } else {
      setAuthorization({ isPermission: 0 });
    }
  }, [formId, authorization?.uRead]);

  useEffect(() => {
    const filtered = data.filter((item) =>
      item.userGroupName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, data]);

  const handleClick = () => {
    if (authorization?.uWrite === 1) {
      if (!encodedFormId) {
        console.error("encodedFormId is missing when navigating to addusergrp");
        navigate("/addusergrp", { state: { encodedFormId: "MjE=" } }); // Fallback to MjE=
      } else {
        navigate("/addusergrp", { state: { encodedFormId } });
      }
    } else {
      // alert("You are not authorized to add user group");
      setSnackbarMessage('You are not authorized to add user group');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleEdit = (userGroupId) => {
    if (authorization?.uModify === 1) {
      navigate(`/editusergrp/${userGroupId}`, {
        state: { encodedFormId: encodedFormId || "MjE=" },
      });
    } else {
      // alert("You are not authorized to edit");
      setSnackbarMessage('You are not authorized to edit');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleShow = (userGroupId, userGroupName) => {
    if (authorization?.uDelete === 1) {
      setSelectedUserGroup({ userGroupId, userGroupName });
      setShowModal(true);
    } else {
      // alert("You are not authorized to delete");
       setSnackbarMessage('You are not authorized to delete');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleClose = () => setShowModal(false);

const handleDelete = async () => {
  if (authorization?.uDelete !== 1) {
    // alert("You are not authorized to delete");
     setSnackbarMessage('You are not authorized to delete');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    return;
  }
  try {
    setIsLoading(true); // Start loading
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(
      `${apiUrl}/CPanel/Id?UserGroupId=${selectedUserGroup.userGroupId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );
    if (response.ok) {
      fetchData();
      setShowModal(false);
      setSnackbarMessage('User Group deleted successfully!');
      setOpenSnackbar(true);
      setSnackbarSeverity("success");
    } else {
      console.error("Delete failed");
      alert("Failed to delete user group");
    }
  } catch (error) {
    console.error("API request error:", error);
    // alert("An error occurred. Please try again later.");
     setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
  } finally {
    setIsLoading(false); // Stop loading
  }
};


  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );


  return (
    <Container>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          maxWidth: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
          User Group
        </Typography> </Box>


      <Paper
        elevation={3}
        style={{ padding: "20px", paddingTop: "2px", margin: "6px auto", maxWidth: "100%" }}
      >
        <div style={{ display: 'flex', justifyContent: "space-between", alignItems: 'center', margin: "10px auto" }}>

          <TextField
            type="text"
            id="search"
            placeholder="Search"
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />


          <Tooltip
            title={
              authorization.uWrite === 1
                ? ""
                : "You are not authorized to add user group"
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                style={{ height: 53 }}
                onClick={handleClick}
                disabled={authorization.uWrite !== 1}
              >
                <b>ADD NEW USER GROUP</b>
              </Button>
            </span>
          </Tooltip>
        </div>
        <TableContainer component={Paper} style={{ marginTop: "16px" }}>
          <Table

            sx={{
              minWidth: 650,
              border: '1px solid #ccc',
              '& .MuiTableCell-root': {
                borderRight: '1px solid #ccc',
                fontSize: '1rem',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  User Group Name
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #ccc', borderRight: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
           <TableBody>
  {isLoading ? (
    <TableRow>
      <TableCell colSpan={2} align="center">
        <CircularProgress />
      </TableCell>
    </TableRow>
  ) : paginatedData.length > 0 ? (
    paginatedData.map((item) => (
      <TableRow key={item.userGroupId} sx={{ borderTop: '1px solid #ccc' }}>
        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
          {item.userGroupName}
        </TableCell>
        <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
          <Tooltip title={authorization.uModify === 1 ? '' : 'You are not authorized to edit'} arrow>
            <span>
              <Button
                variant="contained"
                color="warning"
                onClick={() => handleEdit(item.userGroupId)}
                sx={{ marginRight: 1 }}
                disabled={authorization.uModify !== 1}
              >
                Edit
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={authorization.uDelete === 1 ? '' : 'You are not authorized to delete'} arrow>
            <span>
              <Button
                variant="contained"
                color="error"
                sx={{ marginLeft: "5px" }}
                onClick={() => handleShow(item.userGroupId, item.userGroupName)}
                disabled={authorization.uDelete !== 1}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    ))
  ) : (
    <TableRow>
      <TableCell colSpan={2} align="center">
        No User Groups Found
      </TableCell>
    </TableRow>
  )}
</TableBody>

          </Table>
        </TableContainer>
        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

      </Paper>

      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete the user with role name{" "}
              <strong>{selectedUserGroup.userGroupName}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

<Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>

    </Container>
  );
}

export default UserGroup;
